import { Button, Icons } from '@sede-enterprise/shell-ds-react-framework';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';
import { useForm, type FieldValues } from 'react-hook-form';
import FormControlCheckbox from 'src/UI/FormComponent/FormControlCheckbox';
import FormControlDatePicker from 'src/UI/FormComponent/FormControlDatePicker';
import FormControlSelect from 'src/UI/FormComponent/FormControlSelect';
import FormControlTextInput from 'src/UI/FormComponent/FormControlTextInput';
import Loader from 'src/UI/Loader';
import { sendNotification } from 'src/UI/Notification';
import Table from 'src/components/FieldNoms/FieldNomination/Table';
import type {
  IColumn,
  ITableFilterValue,
} from 'src/components/FieldNoms/FieldNomination/Table/Table.type';
import { CET_TIME_ZONE, COMMON_MESSAGES } from 'src/constants/Common';
import {
  GET_ORGANIZATIONS,
  GET_PARAMETER_DEFINITIONS,
  GET_TIME_SERIES_VOLUME_TYPES,
  MESSAGES,
  ParameterScope,
  SET_JOB,
} from 'src/constants/EUMarkets';
import {
  formatDateOnly,
  getDefinitionValueKey,
  getValueValidation,
  isDateRangeValid,
} from 'src/components/EUMarkets/SystemAdmin/Locations/AddEditParameter';
import { get, post } from 'src/services/GenesisApi';
import { handleError } from 'src/utils/common/utils';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  fetchJobMessageTypes,
  fetchJobPeriods,
  type JobLookupRecord,
} from 'src/store/reducers/JobLookups';
import type { IOrganization, IParameterDefinition } from 'src/types/EUMarkets.type';
import type { ISelectOptions } from 'src/types/Common.type';
import { JobsModal } from './style';

dayjs.extend(utc);
dayjs.extend(timezone);

const { CheckCircleSolid, Copy, CrossCircle, CrossCircleSolid, SearchCircle } = Icons;
const INLINE_BLOCK = 'inline-block';

export type JobRecord = Record<string, unknown>;

type JobsProps = {
  open: boolean;
  nodeName?: string;
  jobs: JobRecord[];
  isLoading: boolean;
  masterDataId?: number | string;
  onClose: () => void;
  onJobSaved?: () => void;
  onCloneJob?: (job: JobRecord) => void;
};

type JobTableRow = {
  id: string | number;
  job: JobRecord;
  actions: string;
  isActive: boolean;
  direction: string;
  type: string;
  sender: string;
  receiver: string;
  referenceCode: string;
  messagePeriod: string;
  granularity: string;
  validFrom: number | null;
  validTo: number | null;
};

type JobParameter = Record<string, unknown>;

const formatJobValue = (value: unknown): string => {
  if (value == null) {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const formatDate = (value: number | null) =>
  value == null ? '—' : dayjs(value).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY);

const formatDateTime = (value: unknown) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return formatJobValue(value);
  }
  const parsedValue = dayjs.utc(value).tz(CET_TIME_ZONE);
  return parsedValue.isValid() ? parsedValue.format('DD MMM YYYY hh:mm:ss') : formatJobValue(value);
};

const getDateValue = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue.valueOf() : null;
};

const getPeriodProperty = (period: unknown, propertyNames: string[]) => {
  if (typeof period !== 'object' || period === null) {
    return undefined;
  }
  const periodRecord = period as Record<string, unknown>;
  return propertyNames
    .map((propertyName) => periodRecord[propertyName])
    .find((value) => value != null);
};

// Extracts a representative display value (e.g. message type name, period label) from a lookup
// record so the same text is used both as the filter option and for matching row cell values.
// Falls back to the first non-id string field instead of dumping the whole record as JSON, in
// case the record doesn't use any of the expected key names.
const toLookupOptions = (items: JobLookupRecord[], keys: string[]): ISelectOptions[] => {
  const values = new Set<string>();
  items.forEach((item) => {
    const key = keys.find(
      (candidateKey) => item[candidateKey] != null && item[candidateKey] !== '',
    );
    if (key) {
      values.add(formatJobValue(item[key]));
      return;
    }
    const fallbackEntry = Object.entries(item).find(
      ([entryKey, entryValue]) =>
        typeof entryValue === 'string' &&
        entryValue !== '' &&
        !entryKey.toLowerCase().endsWith('id'),
    );
    if (fallbackEntry) {
      values.add(fallbackEntry[1] as string);
    }
  });
  return Array.from(values).map((value) => ({ id: value, name: value }));
};

// 'select' columns require an exact match against the chosen option; 'text' columns use substring search.
const FILTER_COLUMN_TYPES: Record<string, 'text' | 'select'> = {
  direction: 'select',
  type: 'select',
  sender: 'text',
  receiver: 'text',
  referenceCode: 'text',
  messagePeriod: 'select',
  granularity: 'select',
};

// Only the filterable (plain string) columns — excludes `id`/`job`/`validFrom`/`validTo`/`viewDetails`,
// which aren't strings and would otherwise widen row[key] to include JobRecord (an object).
type FilterableJobTableKey = keyof typeof FILTER_COLUMN_TYPES;

const rowMatchesFilters = (row: JobTableRow, filters: ITableFilterValue[]): boolean =>
  filters.every((filter) => {
    const { value } = filter;
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
      return true;
    }
    const cellValue = row[filter.key];
    const cellText = typeof cellValue === 'string' ? cellValue.toLowerCase() : '';
    const filterText = String(value).toLowerCase();
    return FILTER_COLUMN_TYPES[filter.key] === 'text'
      ? cellText.includes(filterText)
      : cellText === filterText;
  });

const getJobRows = (jobs: JobRecord[]): JobTableRow[] =>
  jobs.map((job, index) => {
    const period = job.period;
    const validFrom = getDateValue(
      job.validFrom ?? getPeriodProperty(period, ['validFrom', 'from', 'start']),
    );
    const validTo = getDateValue(
      job.validTo ?? getPeriodProperty(period, ['validTo', 'to', 'end']),
    );

    return {
      id: (job.id as string | number | undefined) ?? index,
      job,
      actions: '',
      isActive: job.isActive === true || job.isActive === 'true',
      direction:
        (typeof job.direction === 'string' ? job.direction : undefined) ??
        (job.isIncoming === true ? 'Incoming' : 'Outgoing'),
      type: formatJobValue(job.messageType ?? job.type),
      sender: formatJobValue(job.senderOrganisationName ?? job.sender),
      receiver: formatJobValue(job.receiverOrganisationName ?? job.receiver),
      referenceCode: formatJobValue(job.referenceCode),
      messagePeriod: formatJobValue(period ?? job.messagePeriod),
      granularity:
        (typeof job.granularity === 'string' ? job.granularity : undefined) ??
        (job.isHourlyGranularity === true ? 'Hour' : 'Day'),
      validFrom,
      validTo,
    };
  });

const GRANULARITY_OPTIONS: ISelectOptions[] = [
  { id: 'Hour', name: 'Hour' },
  { id: 'Day', name: 'Day' },
];
const DIRECTION_OPTIONS: ISelectOptions[] = [
  { id: 'Incoming', name: 'Incoming' },
  { id: 'Outgoing', name: 'Outgoing' },
];

const getColumns = (
  onViewDetails: (job: JobRecord) => void,
  onCloneJob: (job: JobRecord) => void,
  messageTypeOptions: ISelectOptions[],
  periodOptions: ISelectOptions[],
): IColumn[] => [
  {
    accessorKey: 'actions',
    header: '',
    width: 80,
    cell: ({ row }) => (
      <div className='job-table-actions-cell'>
        <Button
          icon={<SearchCircle />}
          iconOnly
          variant='transparent'
          aria-label='View details'
          title='View details'
          data-testid={`market-position-new-view-details-${row.original.id}`}
          onClick={() => onViewDetails(row.original.job)}
        />
        <Button
          icon={<Copy />}
          iconOnly
          variant='transparent'
          aria-label='Clone job'
          title='Clone job'
          data-testid={`market-position-new-clone-job-${row.original.id}`}
          onClick={() => onCloneJob(row.original.job)}
        />
        <span
          title={row.original.isActive ? 'Active' : 'Inactive'}
          aria-label={row.original.isActive ? 'Active' : 'Inactive'}
          data-testid={`market-position-new-job-status-${row.original.id}`}
          className='job-status-icon'
        >
          {row.original.isActive ? (
            <CheckCircleSolid
              width={18}
              height={18}
              style={{ color: '#22a06b', display: INLINE_BLOCK }}
            />
          ) : (
            <CrossCircleSolid
              width={18}
              height={18}
              style={{ color: '#e3342f', display: INLINE_BLOCK }}
            />
          )}
        </span>
      </div>
    ),
  },
  {
    accessorKey: 'direction',
    header: 'Direction',
    sorting: true,
    type: 'select',
    optionsData: DIRECTION_OPTIONS,
    filter: { key: 'direction', label: 'Direction' },
  },
  {
    accessorKey: 'type',
    header: 'Type',
    sorting: true,
    type: 'select',
    optionsData: messageTypeOptions,
    filter: { key: 'type', label: 'Type' },
  },
  {
    accessorKey: 'sender',
    header: 'Sender',
    sorting: true,
    type: 'text',
    filter: { key: 'sender', label: 'Sender' },
  },
  {
    accessorKey: 'receiver',
    header: 'Receiver',
    sorting: true,
    type: 'text',
    filter: { key: 'receiver', label: 'Receiver' },
  },
  {
    accessorKey: 'referenceCode',
    header: 'Reference Code',
    sorting: true,
    type: 'text',
    filter: { key: 'referenceCode', label: 'Reference Code' },
  },
  {
    accessorKey: 'messagePeriod',
    header: 'Message Period',
    sorting: true,
    type: 'select',
    optionsData: periodOptions,
    filter: { key: 'messagePeriod', label: 'Message Period' },
  },
  {
    accessorKey: 'granularity',
    header: 'Granularity',
    sorting: true,
    type: 'select',
    optionsData: GRANULARITY_OPTIONS,
    filter: { key: 'granularity', label: 'Granularity' },
  },
  {
    accessorKey: 'validFrom',
    header: 'Valid From',
    sorting: true,
    cell: ({ getValue }) => formatDate(getValue() as number | null),
  },
  {
    accessorKey: 'validTo',
    header: 'Valid To',
    sorting: true,
    cell: ({ getValue }) => formatDate(getValue() as number | null),
  },
];

const JOB_SCOPES: Record<string, { incoming: ParameterScope; outgoing: ParameterScope }> = {
  DNSCP: {
    incoming: ParameterScope.JobIncomingDNSCP,
    outgoing: ParameterScope.JobOutgoingDNSCP,
  },
  AVAIL: {
    incoming: ParameterScope.JobIncomingAVAILY,
    outgoing: ParameterScope.JobOutgoingAVAILY,
  },
  AVAILY: {
    incoming: ParameterScope.JobIncomingAVAILY,
    outgoing: ParameterScope.JobOutgoingAVAILY,
  },
};

const getJobScopeId = (job: JobRecord): ParameterScope | null => {
  const messageType = typeof job.messageType === 'string' ? job.messageType : '';
  const scope = JOB_SCOPES[messageType.toUpperCase()];
  if (!scope) {
    return null;
  }
  return job.isIncoming === true ? scope.incoming : scope.outgoing;
};

const getParameterValue = (parameter: JobParameter) =>
  [
    parameter.valueListId,
    parameter.valueString,
    parameter.valueLong,
    parameter.valueDouble,
    parameter.valueBoolean,
    parameter.valueListValue,
  ].find((value) => value != null && value !== '');

const useParameterDefinitions = (scopeId: ParameterScope | null) => {
  const [definitions, setDefinitions] = useState<IParameterDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setDefinitions([]);
    if (scopeId == null) {
      setIsLoading(false);
      return undefined;
    }

    let isCurrent = true;
    setIsLoading(true);
    get(`${GET_PARAMETER_DEFINITIONS}?scopeId=${scopeId}`)
      .then((response) => {
        if (isCurrent) {
          setDefinitions((response.data ?? []) as IParameterDefinition[]);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setDefinitions([]);
          sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [scopeId]);

  return { definitions, isLoading };
};

const DetailItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className='job-detail-item'>
    <span className='job-detail-label'>{label}</span>
    <span className='job-detail-value'>{children}</span>
  </div>
);

const toOptionalNumber = (value: unknown) => (value == null || value === '' ? null : Number(value));

// List values may not expose `id`; without a defined Option value rc-select falls back to the React key.
const getJobListOptions = (definition: IParameterDefinition): ISelectOptions[] =>
  ((definition.listValues ?? []) as unknown as Record<string, unknown>[])
    .map((listValue) => ({
      id: (listValue.id ??
        listValue.listValueId ??
        listValue.valueListId ??
        listValue.parameterListValueId) as number,
      name: formatJobValue(
        listValue.value ?? listValue.listValue ?? listValue.valueListValue ?? listValue.name,
      ),
    }))
    .filter((option) => option.id != null);

const getMatchingJobParameters = (
  jobParameters: unknown,
  definition: IParameterDefinition,
): JobParameter[] => {
  if (!Array.isArray(jobParameters)) {
    return [];
  }
  return jobParameters.filter(
    (parameter: JobParameter) =>
      parameter != null &&
      (parameter.parameterDefinitionId === definition.id ||
        parameter.parameterName === definition.parameterName),
  );
};

const getExtendedPropertyValue = (
  definition: IParameterDefinition,
  valueKey: string,
  parameter: JobParameter | null,
) => {
  const rawValue = parameter ? getParameterValue(parameter) : undefined;
  if (valueKey === 'valueList') {
    const matchingOption = getJobListOptions(definition).find(
      (option) => String(option.id) === String(rawValue) || option.name === rawValue,
    );
    return matchingOption?.id ?? '';
  }
  if (rawValue != null) {
    return rawValue;
  }
  if (valueKey === 'valueBoolean') {
    return false;
  }
  return '';
};

const getExtendedPropertiesForDefinition = (
  definition: IParameterDefinition,
  jobParameters: unknown,
) => {
  const matchingParameters = getMatchingJobParameters(jobParameters, definition);
  const parameters = matchingParameters.length > 0 ? matchingParameters : [null];
  const valueKey = getDefinitionValueKey(definition);
  return parameters.map((parameter) => ({
    definition,
    valueKey,
    value: getExtendedPropertyValue(definition, valueKey, parameter),
    parameter,
  }));
};

const toJobParameterValueFields = (valueKey: string, value: unknown) => {
  const listId = valueKey === 'valueList' ? toOptionalNumber(value) : null;
  return {
    valueString: valueKey === 'valueString' ? formatJobValue(value ?? '') : null,
    valueLong: valueKey === 'valueLong' ? toOptionalNumber(value) : null,
    valueDouble: valueKey === 'valueDouble' ? toOptionalNumber(value) : null,
    valueBoolean: valueKey === 'valueBoolean' ? value === true : null,
    valueListId: listId != null && Number.isFinite(listId) ? listId : null,
  };
};

const toDateString = (value: unknown) =>
  value ? formatDateOnly(dayjs(value as dayjs.ConfigType)) : null;

const getInitialJobDirection = (job: JobRecord) => {
  if (job.isIncoming === true || job.direction === 'Incoming') {
    return 'Incoming';
  }
  if (job.isIncoming === false || job.direction === 'Outgoing') {
    return 'Outgoing';
  }
  return '';
};

const getInitialJobGranularity = (job: JobRecord) => {
  if (job.isHourlyGranularity === true || job.granularity === 'Hour') {
    return 'Hour';
  }
  if (job.isHourlyGranularity === false || job.granularity === 'Day') {
    return 'Day';
  }
  return '';
};

type JobDetailsProps = {
  job: JobRecord;
  masterDataId?: number | string;
  onClose: () => void;
  onSaved: () => void;
};

const JobDetails = ({ job, masterDataId, onClose, onSaved }: JobDetailsProps) => {
  const { jobMessageTypes, jobPeriods, jobMessageTypesLoading, jobPeriodsLoading } = useAppSelector(
    (state) => state.JobLookups,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [organizations, setOrganizations] = useState<ISelectOptions[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [volumeTypes, setVolumeTypes] = useState<ISelectOptions[]>([]);
  const [volumeTypesLoading, setVolumeTypesLoading] = useState(false);
  const methods = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      isActive: job.isActive === true,
      direction: getInitialJobDirection(job),
      volumeTypeId: job.volumeTypeId ?? job.volumeTypeName ?? '',
      messageTypeId: job.messageTypeId ?? job.messageType ?? '',
      periodId:
        job.periodId ?? getPeriodProperty(job.period, ['periodId', 'id']) ?? job.period ?? '',
      granularity: getInitialJobGranularity(job),
      senderOrganisationId: job.senderOrganisationId ?? job.senderOrganisationName ?? '',
      receiverOrganisationId: job.receiverOrganisationId ?? job.receiverOrganisationName ?? '',
      referenceCode: job.referenceCode ?? '',
      isInverseSign: job.isInverseSign === true,
      validFrom: getDateValue(job.validFrom) == null ? null : dayjs(getDateValue(job.validFrom)),
      validTo: getDateValue(job.validTo) == null ? null : dayjs(getDateValue(job.validTo)),
      parameters: [] as { value: unknown }[],
    },
  });
  const { watch, setValue, getFieldState } = methods;
  const messageTypeOptions = useMemo(
    () =>
      jobMessageTypes.map((item) => ({
        id: (item.messageTypeId ?? item.id) as number,
        name: formatJobValue(
          item.messageTypeCode ?? item.messageTypeName ?? item.messageType ?? item.name,
        ),
      })),
    [jobMessageTypes],
  );
  const periodOptions = useMemo(
    () =>
      jobPeriods.map((item) => ({
        id: (item.periodId ?? item.id) as number,
        name: formatJobValue(item.periodName ?? item.period ?? item.name),
      })),
    [jobPeriods],
  );
  const selectedTypeId = watch('messageTypeId');
  const selectedType = messageTypeOptions.find((option) => option.id === selectedTypeId)?.name;
  const direction = watch('direction');
  const scopeId =
    direction === 'Incoming' || direction === 'Outgoing'
      ? getJobScopeId({
          ...job,
          messageType: selectedType ?? selectedTypeId,
          isIncoming: direction === 'Incoming',
        })
      : null;
  const { definitions, isLoading } = useParameterDefinitions(scopeId);
  const validFrom = watch('validFrom');
  const validTo = watch('validTo');
  const popupContainer = (triggerNode: HTMLElement) =>
    triggerNode.closest<HTMLElement>('.jobs-modal') ?? document.body;
  const extendedProperties = useMemo(
    () =>
      definitions.flatMap((definition) =>
        getExtendedPropertiesForDefinition(definition, job.parameters),
      ),
    [definitions, job.parameters],
  );

  useEffect(() => {
    let isCurrent = true;
    setOrganizationsLoading(true);
    get(GET_ORGANIZATIONS)
      .then((response) => {
        if (isCurrent) {
          setOrganizations(
            ((response.data ?? []) as IOrganization[]).map((organization) => ({
              id: organization.id,
              name: organization.organizationName,
            })),
          );
        }
      })
      .catch(() => {
        if (isCurrent) {
          sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
        }
      })
      .finally(() => {
        if (isCurrent) {
          setOrganizationsLoading(false);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    let isCurrent = true;
    setVolumeTypesLoading(true);
    get(GET_TIME_SERIES_VOLUME_TYPES)
      .then((response) => {
        if (isCurrent) {
          setVolumeTypes(
            ((response.data ?? []) as JobLookupRecord[]).map((volumeType) => ({
              // The renamed lookup endpoint retains its existing response shape.
              id: (volumeType.volumeTypeId ?? volumeType.categoryId ?? volumeType.id) as number,
              name: formatJobValue(
                [
                  volumeType.volumeTypeName,
                  volumeType.volumeTypeCode,
                  volumeType.volumeType,
                  volumeType.categoryName,
                  volumeType.categoryCode,
                  volumeType.category,
                  volumeType.name,
                ].find((value) => value != null),
              ),
            })),
          );
        }
      })
      .catch(() => {
        if (isCurrent) {
          sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
        }
      })
      .finally(() => {
        if (isCurrent) {
          setVolumeTypesLoading(false);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    const fields = [
      ['volumeTypeId', job.volumeTypeId ?? job.volumeTypeName, volumeTypes],
      ['messageTypeId', job.messageTypeId ?? job.messageType, messageTypeOptions],
      [
        'periodId',
        job.periodId ??
          getPeriodProperty(job.period, ['periodId', 'id', 'periodName', 'name']) ??
          job.period,
        periodOptions,
      ],
      [
        'senderOrganisationId',
        job.senderOrganisationId ?? job.senderOrganisationName,
        organizations,
      ],
      [
        'receiverOrganisationId',
        job.receiverOrganisationId ?? job.receiverOrganisationName,
        organizations,
      ],
    ] as const;
    fields.forEach(([name, value, options]) => {
      const option = options.find(
        (item) => String(item.id) === String(value) || item.name === value,
      );
      if (option && !getFieldState(name).isDirty) {
        setValue(name, option.id);
      }
    });
  }, [job, messageTypeOptions, periodOptions, organizations, volumeTypes, setValue, getFieldState]);

  useEffect(() => {
    setValue(
      'parameters',
      extendedProperties.map((parameter) => ({ value: parameter.value })),
    );
  }, [extendedProperties, setValue]);

  const hasStatus = job.modifiedTimeInUtc != null || job.modifiedBy != null;

  const handleSave = (values: FieldValues) => {
    const jobValidFrom = toDateString(values.validFrom);
    const jobValidTo = toDateString(values.validTo);
    const parameterValues = (methods.getValues('parameters') ?? []) as { value: unknown }[];
    setIsSaving(true);
    post(SET_JOB, {
      id: toOptionalNumber(job.id) ?? 0,
      masterDataId: toOptionalNumber(job.masterDataId ?? masterDataId),
      volumeTypeId: toOptionalNumber(values.volumeTypeId),
      senderOrganisationId: Number(values.senderOrganisationId),
      receiverOrganisationId: Number(values.receiverOrganisationId),
      referenceCode: values.referenceCode ?? '',
      messageTypeId: Number(values.messageTypeId),
      periodId: Number(values.periodId),
      isActive: values.isActive === true,
      isIncoming: values.direction === 'Incoming',
      isHourlyGranularity: values.granularity === 'Hour',
      isInverseSign: values.isInverseSign === true,
      validFrom: jobValidFrom,
      validTo: jobValidTo,
      parameters: extendedProperties.map(({ definition, valueKey, parameter }, index) => ({
        parameterDefinitionId: definition.id,
        ...toJobParameterValueFields(valueKey, parameterValues[index]?.value),
        validFrom: toDateString(parameter?.validFrom) ?? jobValidFrom,
        validTo: toDateString(parameter?.validTo) ?? jobValidTo,
      })),
    })
      .then(() => {
        sendNotification({ type: 'success', message: MESSAGES.SAVED_JOB });
        onSaved();
      })
      .catch((error) => {
        handleError(error, MESSAGES.SAVED_JOB_FAILED);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <section className='job-details' data-testid='market-position-new-job-details'>
      <div className='job-details-header'>
        <h3>Job details</h3>
        <Button
          icon={<CrossCircle />}
          iconOnly
          variant='transparent'
          aria-label='Hide job details'
          data-testid='market-position-new-hide-job-details'
          onClick={onClose}
        />
      </div>
      <div className='job-details-layout'>
        <div className='job-details-column'>
          <h4>Job properties</h4>
          <div className='job-details-fields'>
            <FormControlCheckbox
              methods={methods}
              label='Active'
              controllerProps={{ name: 'isActive' }}
            />
            <FormControlSelect
              methods={methods}
              label='Direction'
              controllerProps={{ name: 'direction', rules: { required: 'Direction is required.' } }}
              data={DIRECTION_OPTIONS}
              isLoading={false}
              selectProps={{ getPopupContainer: popupContainer }}
            />
            <FormControlSelect
              methods={methods}
              label='Volume Type'
              controllerProps={{ name: 'volumeTypeId' }}
              data={volumeTypes}
              isLoading={volumeTypesLoading}
              selectProps={{ getPopupContainer: popupContainer, showSearch: true }}
            />
            <FormControlSelect
              methods={methods}
              label='Type'
              controllerProps={{ name: 'messageTypeId', rules: { required: 'Type is required.' } }}
              data={messageTypeOptions}
              isLoading={jobMessageTypesLoading}
              selectProps={{ getPopupContainer: popupContainer }}
            />
            <FormControlSelect
              methods={methods}
              label='Message Period'
              controllerProps={{
                name: 'periodId',
                rules: { required: 'Message period is required.' },
              }}
              data={periodOptions}
              isLoading={jobPeriodsLoading}
              selectProps={{ getPopupContainer: popupContainer }}
            />
            <FormControlSelect
              methods={methods}
              label='Granularity'
              controllerProps={{
                name: 'granularity',
                rules: { required: 'Granularity is required.' },
              }}
              data={GRANULARITY_OPTIONS}
              isLoading={false}
              selectProps={{ getPopupContainer: popupContainer }}
            />
            <FormControlSelect
              methods={methods}
              label='Sender'
              controllerProps={{
                name: 'senderOrganisationId',
                rules: { required: 'Sender is required.' },
              }}
              data={organizations}
              isLoading={organizationsLoading}
              selectProps={{ getPopupContainer: popupContainer }}
            />
            <FormControlSelect
              methods={methods}
              label='Receiver'
              controllerProps={{
                name: 'receiverOrganisationId',
                rules: { required: 'Receiver is required.' },
              }}
              data={organizations}
              isLoading={organizationsLoading}
              selectProps={{ getPopupContainer: popupContainer }}
            />
            <FormControlTextInput
              methods={methods}
              label='Reference code'
              controllerProps={{ name: 'referenceCode' }}
            />
            <FormControlCheckbox
              methods={methods}
              label='Inverse Sign'
              controllerProps={{ name: 'isInverseSign' }}
            />
          </div>
        </div>
        <div className='job-details-column'>
          {isLoading && (
            <div className='jobs-loader'>
              <Loader />
            </div>
          )}
          {!isLoading && extendedProperties.length > 0 && (
            <div className='job-detail-section'>
              <h4>Extended Properties</h4>
              <div className='job-details-fields extended-properties-fields'>
                {extendedProperties.map(({ definition, valueKey }, index) => {
                  const name = `parameters.${index}.value`;
                  const label = definition.parameterName;
                  let control: React.ReactNode;
                  if (valueKey === 'valueBoolean') {
                    control = (
                      <FormControlCheckbox
                        methods={methods}
                        label={label}
                        controllerProps={{
                          name,
                          rules: {
                            validate: (value) =>
                              typeof value === 'boolean' || MESSAGES.REQUIRED_PARAMETER_VALUE,
                          },
                        }}
                      />
                    );
                  } else if (valueKey === 'valueList') {
                    control = (
                      <FormControlSelect
                        methods={methods}
                        label={label}
                        controllerProps={{ name, rules: getValueValidation(valueKey) }}
                        data={getJobListOptions(definition)}
                        isLoading={false}
                        selectProps={{ getPopupContainer: popupContainer }}
                      />
                    );
                  } else {
                    control = (
                      <FormControlTextInput
                        methods={methods}
                        label={label}
                        controllerProps={{ name, rules: getValueValidation(valueKey) }}
                        inputProps={{
                          type: valueKey === 'valueString' ? 'text' : 'number',
                          step: valueKey === 'valueLong' ? '1' : 'any',
                        }}
                      />
                    );
                  }
                  return <div key={`${scopeId}-${definition.id}-${index}`}>{control}</div>;
                })}
              </div>
            </div>
          )}
          {hasStatus && (
            <div className='job-detail-section'>
              <h4>Status</h4>
              <DetailItem label='Modified Time'>{formatDateTime(job.modifiedTimeInUtc)}</DetailItem>
              <DetailItem label='Modified By'>{formatJobValue(job.modifiedBy)}</DetailItem>
            </div>
          )}
          <div className='job-detail-section'>
            <h4>Validity</h4>
            <div className='job-details-fields'>
              <FormControlDatePicker
                methods={methods}
                label='Valid From'
                controllerProps={{
                  name: 'validFrom',
                  rules: {
                    required: MESSAGES.REQUIRED_VALID_FROM,
                    validate: (value) =>
                      isDateRangeValid(value, validTo) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                  },
                }}
                handleOnChange={async () => {
                  await methods.trigger('validTo');
                }}
              />
              <FormControlDatePicker
                methods={methods}
                label='Valid To'
                controllerProps={{
                  name: 'validTo',
                  rules: {
                    validate: (value) =>
                      isDateRangeValid(validFrom, value) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                  },
                }}
                inputProps={{
                  disabledDate: (current) => !!validFrom && current.isBefore(validFrom, 'day'),
                }}
                handleOnChange={async () => {
                  await methods.trigger('validFrom');
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='job-details-actions'>
        <Button
          type='button'
          variant='outlined'
          disabled={isSaving}
          data-testid='market-position-new-cancel-job'
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type='button'
          loading={isSaving}
          disabled={isSaving || isLoading}
          data-testid='market-position-new-save-job'
          onClick={methods.handleSubmit(handleSave)}
        >
          Save
        </Button>
      </div>
    </section>
  );
};

export const Jobs = ({
  open,
  nodeName,
  jobs,
  isLoading,
  masterDataId,
  onClose,
  onJobSaved,
  onCloneJob,
}: JobsProps) => {
  const dispatch = useAppDispatch();
  const {
    jobPeriods,
    jobPeriodsLoaded,
    jobPeriodsLoading,
    jobMessageTypes,
    jobMessageTypesLoaded,
    jobMessageTypesLoading,
  } = useAppSelector((state) => state.JobLookups);
  const [selectedJob, setSelectedJob] = useState<JobRecord | null>(null);
  const [tableFilters, setTableFilters] = useState<ITableFilterValue[]>([]);
  const jobRows = useMemo(() => getJobRows(jobs), [jobs]);
  const filteredJobRows = useMemo(
    () => jobRows.filter((row) => rowMatchesFilters(row, tableFilters)),
    [jobRows, tableFilters],
  );
  const messageTypeOptions = useMemo(
    () =>
      toLookupOptions(jobMessageTypes, [
        'messageTypeCode',
        'messageTypeName',
        'messageTypeDescription',
        'messageType',
        'description',
        'type',
        'name',
        'value',
      ]),
    [jobMessageTypes],
  );
  const periodOptions = useMemo(
    () => toLookupOptions(jobPeriods, ['periodName', 'period', 'name', 'value']),
    [jobPeriods],
  );

  const handleCloneJob = (job: JobRecord) => {
    if (onCloneJob) {
      onCloneJob(job);
      return;
    }
    const { id: _id, modifiedTimeInUtc: _mt, modifiedBy: _mb, ...clonedJob } = job;
    setSelectedJob({
      ...clonedJob,
      id: null,
    });
  };

  useEffect(() => {
    setSelectedJob(null);
    setTableFilters([]);
  }, [jobs]);

  // Job lookup data (periods/message types) is fetched once, the first time the modal is opened
  // from any screen, and cached in redux so every later open reuses it instead of re-requesting.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (!jobPeriodsLoaded && !jobPeriodsLoading) {
      dispatch(fetchJobPeriods());
    }
    if (!jobMessageTypesLoaded && !jobMessageTypesLoading) {
      dispatch(fetchJobMessageTypes());
    }
  }, [
    open,
    dispatch,
    jobPeriodsLoaded,
    jobPeriodsLoading,
    jobMessageTypesLoaded,
    jobMessageTypesLoading,
  ]);

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <div className='jobs-loader'>
        <Loader />
      </div>
    );
  } else if (selectedJob) {
    content = (
      <JobDetails
        job={selectedJob}
        masterDataId={masterDataId}
        onClose={() => setSelectedJob(null)}
        onSaved={() => {
          setSelectedJob(null);
          onJobSaved?.();
        }}
      />
    );
  } else {
    content = (
      <>
        <div className='jobs-actions'>
          <Button data-testid='market-position-new-create-job' onClick={() => setSelectedJob({})}>
            Create Job
          </Button>
        </div>
        <Table
          data={filteredJobRows}
          columns={getColumns(setSelectedJob, handleCloneJob, messageTypeOptions, periodOptions)}
          pagination={false}
          totalRecords={filteredJobRows.length}
          maxHeight={480}
          onTableStateChange={(state) => setTableFilters(state.filters)}
          getFilterPopupContainer={(triggerNode) =>
            triggerNode.closest('.jobs-modal') ?? document.body
          }
        />
      </>
    );
  }

  return (
    <JobsModal
      open={open}
      onClose={onClose}
      title={nodeName ? `Send and receive jobs: ${nodeName}` : 'Send and receive jobs'}
      isDraggable
      width={1250}
      maskClosable={false}
      className='jobs-modal'
      footer={
        selectedJob ? null : (
          <div className='button-style'>
            <Button
              variant='outlined'
              onClick={onClose}
              data-testid='market-position-new-jobs-close'
            >
              Close
            </Button>
          </div>
        )
      }
      data-testid='market-position-new-jobs-modal'
    >
      {content}
    </JobsModal>
  );
};
